import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { 
  FileText, Target, Shield, ArrowRight, Bot, 
  CheckCircle, Mail, Briefcase, Zap, Search,
  Clock, XCircle, Share2, Globe, HeartHandshake, Check, AlertCircle, RefreshCw, FileSearch, Edit3, Lock,
  Filter, PenTool, Download
} from 'lucide-react';

export function LandingFeatures() {
  const [isSharing, setIsSharing] = useState(false);

  return (
    <>
      <section className="mt-40 px-6 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-slate-900 dark:text-white mb-4">Most applications fail before anyone reads them.</h2>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">The modern job hunt is broken. Finding open roles that actually fit your profile is exhausting, and rewriting your resume for every single one takes hours. You need a better way.</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 p-6 rounded-2xl">
            <div className="w-10 h-10 rounded-full bg-red-50 dark:bg-red-500/10 flex items-center justify-center mb-4">
              <AlertCircle className="w-5 h-5 text-red-500" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">The Endless Search</h3>
            <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">Scouring job boards for hours only to find roles that are already closed or wildly mismatched.</p>
          </div>
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 p-6 rounded-2xl">
            <div className="w-10 h-10 rounded-full bg-orange-50 dark:bg-orange-500/10 flex items-center justify-center mb-4">
              <Clock className="w-5 h-5 text-orange-500" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">The Time Drain</h3>
            <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">Manually rewriting bullets and drafting cover letters for every application takes punishingly long.</p>
          </div>
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 p-6 rounded-2xl">
            <div className="w-10 h-10 rounded-full bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center mb-4">
              <Filter className="w-5 h-5 text-blue-500" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">ATS Filtering</h3>
            <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">Applicant Tracking Systems ruthlessly discard resumes that lack proper keyword structured density.</p>
          </div>
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 p-6 rounded-2xl">
            <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
              <Target className="w-5 h-5 text-slate-500 dark:text-slate-400" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Generic Ignorance</h3>
            <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">Sending the same base resume everywhere means you never highlight the exact skills each company prioritizes.</p>
          </div>
        </div>
      </section>

      <section id="how-it-works" className="mt-40 bg-white/50 dark:bg-[#080B17] border-y border-slate-200/50 dark:border-slate-800/50 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-slate-900 dark:text-white mb-4">ResumePro helps you apply <span className="text-emerald-600 dark:text-emerald-400">smarter, not harder.</span></h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">A seamless workflow designed for serious job seekers.</p>
          </div>

          <div className="grid md:grid-cols-4 gap-8 relative">
             <div className="hidden md:block absolute top-[28px] left-[12%] right-[12%] h-[2px] bg-gradient-to-r from-emerald-500/0 via-emerald-500/30 to-emerald-500/0" />
             
             {[
               { step: '01', title: 'Upload Base Resume', desc: 'Securely upload your master resume or LinkedIn PDF.', icon: <FileText className="w-6 h-6 text-slate-600 dark:text-slate-300" /> },
               { step: '02', title: 'Find or Paste Roles', desc: 'Let AI search for matched open roles, or paste a specific job URL.', icon: <Briefcase className="w-6 h-6 text-slate-600 dark:text-slate-300" /> },
               { step: '03', title: 'Match & Tailor', desc: 'Our engine analyzes the gap and rewrites your bullets to perfectly align.', icon: <PenTool className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />, highlight: true },
               { step: '04', title: 'Review & Apply', desc: 'Review changes, make final tweaks, and apply with a perfect PDF.', icon: <Download className="w-6 h-6 text-slate-600 dark:text-slate-300" /> }
             ].map((item, i) => (
               <div key={i} className="relative flex flex-col items-center text-center group">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-6 relative z-10 transition-transform group-hover:scale-110 dark:shadow-md border ${item.highlight ? 'bg-emerald-100 dark:bg-emerald-900/50 border-emerald-500' : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700'}`}>
                    {item.icon}
                  </div>
                  <div className="absolute top-0 right-4 text-6xl font-black text-slate-100 dark:text-slate-800/30 -z-10 select-none pointer-events-none">{item.step}</div>
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">{item.title}</h3>
                  <p className="text-slate-600 dark:text-slate-400 text-sm">{item.desc}</p>
               </div>
             ))}
          </div>
        </div>
      </section>

      <section className="mt-32 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
             <span className="text-emerald-600 dark:text-emerald-400 font-bold tracking-widest uppercase text-sm mb-4 inline-block px-4 py-1 border border-emerald-500/30 rounded-full bg-emerald-500/10">Interactive Workspace</span>
             <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-slate-900 dark:text-white mt-4">From finding the job, to tailoring the resume.</h2>
          </div>
          
          <div className="rounded-[2rem] bg-slate-900 dark:bg-[#0A0E1A] p-2 md:p-4 border border-slate-800 dark:shadow-lg overflow-hidden relative">
            <div className="flex items-center px-4 py-3 border-b border-white/10 mb-4 bg-slate-950/50 rounded-t-xl">
               <div className="flex gap-2">
                 <div className="w-3 h-3 rounded-full bg-red-500/80" />
                 <div className="w-3 h-3 rounded-full bg-amber-500/80" />
                 <div className="w-3 h-3 rounded-full bg-emerald-500/80" />
               </div>
            </div>

            <div className="grid md:grid-cols-12 gap-4 h-[500px]">
               {/* Left Panel */}
               <div className="hidden md:flex md:col-span-4 bg-slate-800/50 rounded-xl p-5 border border-white/5 flex-col">
                 <div className="flex items-center justify-between border-b border-white/10 pb-3 mb-4">
                   <h4 className="text-white font-bold text-sm uppercase tracking-wider">AI Discovered Roles</h4>
                   <span className="bg-emerald-500/20 text-emerald-400 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                     <Search className="w-3 h-3" /> Live
                   </span>
                 </div>
                 <div className="space-y-4">
                   <div className="bg-slate-900 border border-emerald-500/30 rounded-lg p-3 relative shadow-[0_0_15px_rgba(16,185,129,0.1)]">
                     <div className="absolute -top-2 -right-2 bg-emerald-500 text-slate-900 text-[9px] font-black uppercase px-2 py-0.5 rounded-full">Top Match</div>
                     <div className="text-sm text-white font-bold mb-1">Senior Frontend Engineer</div>
                     <div className="text-xs text-slate-400 mb-2">Acme Corp • Remote</div>
                     <div className="text-[10px] text-slate-300 line-clamp-2">
                       Seeking an experienced React developer for scalable SaaS interfaces...
                     </div>
                   </div>
                   <div className="bg-slate-900/50 border border-white/5 rounded-lg p-3 py-2.5 opacity-50">
                     <div className="text-sm text-white font-bold mb-0.5">Frontend Developer</div>
                     <div className="text-xs text-slate-400">TechFlow • New York (Hybrid)</div>
                   </div>
                   <div className="bg-slate-900/50 border border-white/5 rounded-lg p-3 py-2.5 opacity-30">
                     <div className="text-sm text-white font-bold mb-0.5">UI Engineer</div>
                     <div className="text-xs text-slate-400">Innovate AI • San Francisco</div>
                   </div>
                 </div>
               </div>

               {/* Center Panel */}
               <div className="md:col-span-3 bg-slate-800/50 rounded-xl p-5 border border-white/5 flex flex-col items-center justify-center text-center">
                  <div className="relative mb-6">
                    <svg className="w-32 h-32 transform -rotate-90">
                      <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="8" fill="transparent" className="text-slate-700" />
                      <motion.circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="8" fill="transparent" strokeDasharray="351.85" initial={{ strokeDashoffset: 351.85 }} whileInView={{ strokeDashoffset: 70.37 }} transition={{ duration: 1.5, delay: 0.5 }} className="text-emerald-500 drop-shadow-[0_0_8px_rgba(16,185,129,0.5)]" />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <motion.span initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ delay: 1 }} className="text-3xl font-black text-white">92</motion.span>
                      <span className="text-[10px] uppercase font-bold text-emerald-500">Fit Score</span>
                    </div>
                  </div>
                  <div className="w-full text-left space-y-2">
                    <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">Identified Gaps</div>
                    <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs p-2 rounded flex items-center justify-between">
                       TypeScript <Check className="w-3 h-3" />
                    </div>
                    <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs p-2 rounded flex items-center justify-between">
                       Data Vis (D3) <Check className="w-3 h-3" />
                    </div>
                    <div className="bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs p-2 rounded flex items-center justify-between">
                       SaaS Architecture <RefreshCw className="w-3 h-3" />
                    </div>
                  </div>
               </div>

               {/* Right Panel */}
               <div className="hidden sm:flex md:col-span-5 bg-white rounded-xl p-6 flex-col overflow-hidden relative dark:shadow-inner">
                  <div className="flex border-b border-slate-200 pb-2 mb-4 gap-4">
                    <div className="text-sm font-bold text-slate-800 border-b-2 border-emerald-500 pb-1">Tailored Resume</div>
                    <div className="text-sm font-semibold text-slate-400 pb-1">Cover Letter</div>
                  </div>
                  <div className="flex-1 mt-2 text-slate-800 space-y-4">
                     <div>
                       <h3 className="text-sm font-black uppercase text-slate-900 border-b border-slate-200 pb-1 mb-2">Experience</h3>
                       <div className="mb-2">
                         <div className="flex justify-between items-baseline mb-1">
                           <div className="font-bold text-sm">Senior Developer</div>
                           <div className="text-xs text-slate-500">2020 - Present</div>
                         </div>
                         <ul className="list-disc pl-4 text-xs space-y-1.5 text-slate-600">
                           <li>
                             Architected scalable <span className="bg-emerald-100 text-emerald-800 font-medium px-1 rounded">SaaS</span> interfaces using <span className="bg-emerald-100 text-emerald-800 font-medium px-1 rounded">React</span> and <span className="bg-emerald-100 text-emerald-800 font-medium px-1 rounded">TypeScript</span>, accelerating feature delivery by 40%.
                           </li>
                           <li>
                             Integrated complex <span className="bg-emerald-100 text-emerald-800 font-medium px-1 rounded">data visualization</span> dashboards utilizing <span className="bg-emerald-100 text-emerald-800 font-medium px-1 rounded">D3.js</span> to simplify analytics for enterprise clients.
                           </li>
                         </ul>
                       </div>
                     </div>
                  </div>
               </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mt-32 max-w-5xl mx-auto px-6">
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[2rem] p-8 md:p-12 dark:shadow-sm">
           <div className="text-center mb-12">
             <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-3">AI improves positioning, not facts.</h2>
             <p className="text-slate-600 dark:text-slate-400 max-w-xl mx-auto">ResumePro enhances how your real experience is presented, making it instantly relevant to the recruiter. We focus on clarity and structure—without inventing skills you don't have.</p>
           </div>

           <div className="flex flex-col md:flex-row gap-6">
             <div className="flex-1 bg-white border border-slate-200 dark:bg-red-950/20 dark:border-red-900/30 rounded-2xl p-6 relative">
               <div className="absolute top-4 right-4 bg-slate-100 dark:bg-red-900/50 text-red-600 dark:text-red-400 text-xs font-bold px-2 py-1 rounded uppercase flex items-center gap-1">
                 <XCircle className="w-3 h-3" /> Generic
               </div>
               <h4 className="text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-4">Before</h4>
               <div className="text-slate-800 dark:text-slate-200 text-lg font-medium">
                 "Built frontend features using React."
               </div>
             </div>

             <div className="flex items-center justify-center -my-2 md:-mx-2 z-10">
               <div className="bg-white dark:bg-slate-800 rounded-full p-2 border border-slate-200 dark:border-slate-700 dark:shadow-sm dark:shadow-emerald-500/20 text-emerald-500">
                 <ArrowRight className="w-5 h-5 hidden md:block" />
                 <ArrowRight className="w-5 h-5 block md:hidden rotate-90" />
               </div>
             </div>

             <div className="flex-1 bg-white border border-slate-200 dark:bg-emerald-950/20 dark:border-emerald-900/30 rounded-2xl p-6 relative dark:shadow-[inset_0_0_20px_rgba(16,185,129,0.05)]">
               <div className="absolute top-4 right-4 bg-slate-100 dark:bg-emerald-900/50 text-emerald-600 dark:text-emerald-400 text-xs font-bold px-2 py-1 rounded uppercase flex items-center gap-1">
                 <CheckCircle className="w-3 h-3" /> Tailored
               </div>
               <h4 className="text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-4">After</h4>
               <div className="text-slate-900 dark:text-white text-lg font-bold leading-relaxed">
                 "Built scalable <span className="text-emerald-600 dark:text-emerald-400">React and TypeScript</span> interfaces for data-heavy <span className="text-emerald-600 dark:text-emerald-400">SaaS</span> workflows, improving usability and delivery speed."
               </div>
             </div>
           </div>
           
           <div className="mt-8 flex items-center justify-center gap-2 text-sm text-slate-500 font-medium">
              <Shield className="w-4 h-4 text-emerald-500" /> ResumePro should enhance your real experience, it doesn't invent it.
           </div>
        </div>
      </section>

      <section className="mt-40 px-6 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-3 text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">Everything you need to land the interview.</h2>
          </div>

          {[
            { 
              icon: <Briefcase className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />,
              title: "AI Job Discovery",
              desc: "Describe your ideal role, and let our intelligent engine scour the web to find open, perfectly matched positions just for you."
            },
            { 
              icon: <Target className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />,
              title: "AI Resume Tailoring",
              desc: "Intelligently rewrites your bullet points to emphasize the exact skills and experience the job description explicitly asks for."
            },
            { 
              icon: <Mail className="w-6 h-6 text-blue-600 dark:text-blue-400" />,
              title: "Custom Cover Letters",
              desc: "Generates tailored cover letters that sound human, connecting your past achievements directly to their current needs."
            },
            { 
              icon: <FileSearch className="w-6 h-6 text-amber-600 dark:text-amber-400" />,
              title: "Deep ATS Analysis",
              desc: "Instantly extracts core technical and soft skill requirements, scoring your resume's likelihood of passing resume screening."
            },
            { 
              icon: <Edit3 className="w-6 h-6 text-teal-600 dark:text-teal-400" />,
              title: "Fully Editable Output",
              desc: "Never lose control. Review, tweak, or completely rewrite any AI suggestion before finalizing your application."
            },
            { 
              icon: <Lock className="w-6 h-6 text-slate-600 dark:text-slate-400" />,
              title: "Privacy-Conscious",
              desc: "Your master resume data remains securely stored in your browser's local storage. We prioritize your professional privacy."
            }
          ].map((feat, i) => (
            <div key={i} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-8 rounded-3xl dark:hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-slate-50 dark:bg-slate-800 rounded-xl flex items-center justify-center mb-6">
                {feat.icon}
              </div>
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">{feat.title}</h3>
              <p className="text-slate-600 dark:text-slate-400 leading-relaxed text-sm">{feat.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-32 bg-slate-100 dark:bg-[#0A0D18] py-24 border-y border-slate-200/50 dark:border-slate-800/50">
        <div className="max-w-7xl mx-auto px-6">
           <div className="grid md:grid-cols-2 gap-16 items-center">
              <div>
                 <h2 className="text-3xl md:text-5xl font-bold text-slate-900 dark:text-white mb-6 leading-tight">Built for serious career growth.</h2>
                 <p className="text-lg text-slate-600 dark:text-slate-400 mb-8">Whether you are pivoting to a new industry or aiming for a senior role, context is everything. Generic applications get ignored; tailored applications get interviews.</p>
                 
                 <ul className="space-y-4">
                   {[
                     "Software Engineers & Tech Professionals",
                     "Career Switchers & Pivoters",
                     "Remote & International Applicants",
                     "High-Volume Job Seekers",
                     "Designers & Product Managers"
                   ].map((item, i) => (
                     <li key={i} className="flex items-center gap-3 text-slate-800 dark:text-slate-300 font-medium">
                       <div className="w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                         <Check className="w-4 h-4" />
                       </div>
                       {item}
                     </li>
                   ))}
                 </ul>
              </div>
              
              <div className="relative">
                 <div className="absolute inset-0 bg-gradient-to-tr from-emerald-500/10 to-teal-500/10 rounded-3xl transform rotate-3" />
                 <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-8 dark:shadow-sm relative z-10 w-full">
                    <div className="flex gap-4 items-center mb-6">
                      <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                         <Shield className="w-6 h-6" />
                      </div>
                      <div>
                        <div className="font-bold text-slate-900 dark:text-white">Your Data is Secure</div>
                        <div className="text-sm text-slate-500">100% on-device processing</div>
                      </div>
                    </div>
                    <p className="text-slate-600 dark:text-slate-400 mb-6 font-medium">We believe your career history belongs to you. ResumePro operates efficiently and respects your professional privacy.</p>
                    
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
                         <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">No data scraping</span>
                         <CheckCircle className="w-5 h-5 text-emerald-500" />
                      </div>
                      <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
                         <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">No credit card required to try</span>
                         <CheckCircle className="w-5 h-5 text-emerald-500" />
                      </div>
                      <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
                         <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Local-first architecture</span>
                         <CheckCircle className="w-5 h-5 text-emerald-500" />
                      </div>
                    </div>
                 </div>
              </div>
           </div>
        </div>
      </section>

      <section className="mt-20 px-6">
        <div className="max-w-4xl mx-auto text-center py-16">
           <div className="w-16 h-16 rounded-full bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-500 mx-auto mb-6">
             <HeartHandshake className="w-8 h-8" />
           </div>
           <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4">Help a friend stop sending the same resume everywhere.</h2>
           <p className="text-lg text-slate-600 dark:text-slate-400 mb-8 max-w-2xl mx-auto">Know someone struggling with job applications? One better resume can change their next interview. Share ResumePro with them.</p>
           <button 
             disabled={isSharing}
             onClick={async () => {
              if (isSharing) return;
              setIsSharing(true);
              try {
                if (navigator.share) { 
                  await navigator.share({ title: 'ResumePro', text: 'Tailor your resume with AI instantly!', url: window.location.href }); 
                } else { 
                  await navigator.clipboard.writeText(window.location.href); 
                  alert("Link copied to clipboard!"); 
                }
              } catch (error) {} finally {
                setIsSharing(false);
              }
           }} className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 dark:bg-indigo-500 border border-transparent rounded-full font-bold text-white shadow-sm hover:bg-indigo-700 dark:hover:bg-indigo-600 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
             <Share2 className="w-4 h-4" /> Share ResumePro
           </button>
        </div>
      </section>

      <section className="mt-20 px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="relative rounded-[2.5rem] bg-[#0A0D15] border border-slate-800/80 overflow-hidden dark:shadow-[0_30px_100px_-15px_rgba(0,0,0,0.6)] group">
            <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.05] mix-blend-overlay pointer-events-none" />
            
            <div className="flex flex-col lg:flex-row dark:shadow-inner relative z-10">
              <div className="flex-1 p-10 md:p-16 lg:p-20 flex flex-col justify-center relative">
                <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-emerald-500/20 to-transparent" />
                <div className="absolute bottom-0 left-0 w-[1px] h-full bg-gradient-to-b from-transparent via-emerald-500/20 to-transparent" />

                <div className="inline-flex max-w-fit items-center justify-center gap-2 px-3 py-1.5 rounded-full bg-slate-900 border border-slate-800 text-slate-300 mb-8 dark:shadow-sm">
                   <Shield className="w-3.5 h-3.5 text-emerald-500" />
                   <span className="text-xs font-semibold tracking-wide uppercase">Privacy First Architecture</span>
                </div>

                <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 tracking-tight leading-[1.05] drop-shadow-sm">
                  Stop the endless search and <span className="text-slate-500 line-through decoration-emerald-500 decoration-4">generic</span> resumes.
                </h2>
                
                <p className="text-lg text-slate-400 mb-10 max-w-md font-medium leading-relaxed">
                  Join smart professionals who use ResumePro to find matching jobs, craft the perfect tailored pitch, and secure more interviews.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 items-center">
                  <Link to="/app" className="inline-flex px-8 py-4 bg-white text-slate-900 hover:bg-slate-200 rounded-full text-base font-bold transition-all dark:shadow-[0_0_20px_rgba(255,255,255,0.1)] dark:hover:shadow-[0_0_30px_rgba(255,255,255,0.2)] items-center justify-center gap-3 hover:-translate-y-0.5 group">
                    Get Started Free
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <span className="text-sm font-medium text-slate-500">
                    No credit card required.
                  </span>
                </div>
              </div>

              <div className="flex-1 bg-[#11141D] border-l border-slate-800/80 p-8 md:p-12 relative overflow-hidden hidden md:flex flex-col items-center justify-center min-h-[400px]">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-emerald-500/20 rounded-full blur-[80px] pointer-events-none group-hover:bg-emerald-500/30 transition-colors duration-1000" />
                
                <div className="relative w-full max-w-sm">
                  <motion.div 
                    initial={{ y: 20, opacity: 0, rotate: -4 }}
                    whileInView={{ y: 0, opacity: 1, rotate: -4 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.1 }}
                    className="absolute -top-12 -left-6 right-6 bg-slate-900 border border-slate-800 rounded-2xl p-5 dark:shadow-xl"
                  >
                    <div className="flex items-center gap-3 mb-4 opacity-50">
                      <div className="w-8 h-8 rounded-lg bg-indigo-500/20 flex items-center justify-center text-indigo-400">
                        <FileText className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="h-2.5 w-24 bg-slate-800 rounded-full mb-1.5" />
                        <div className="h-2 w-16 bg-slate-800 rounded-full" />
                      </div>
                    </div>
                    <div className="space-y-2 opacity-30">
                      <div className="h-2 w-full bg-slate-800 rounded-full" />
                      <div className="h-2 w-5/6 bg-slate-800 rounded-full" />
                      <div className="h-2 w-4/6 bg-slate-800 rounded-full" />
                    </div>
                  </motion.div>

                  <motion.div 
                    initial={{ y: 40, opacity: 0, rotate: 2 }}
                    whileInView={{ y: 0, opacity: 1, rotate: 2 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    className="absolute -top-6 -right-6 left-6 bg-slate-900 border border-slate-800 rounded-2xl p-5 dark:shadow-xl"
                  >
                    <div className="flex items-center gap-3 mb-4 opacity-70">
                      <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center text-amber-400">
                        <Check className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="h-2.5 w-32 bg-slate-800 rounded-full mb-1.5" />
                        <div className="h-2 w-20 bg-slate-800 rounded-full" />
                      </div>
                    </div>
                    <div className="space-y-3 opacity-50">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-slate-800" />
                        <div className="h-2 w-full bg-slate-800 rounded-full" />
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-slate-800" />
                        <div className="h-2 w-4/5 bg-slate-800 rounded-full" />
                      </div>
                    </div>
                  </motion.div>

                  <motion.div 
                    initial={{ y: 60, opacity: 0, rotate: 0 }}
                    whileInView={{ y: 0, opacity: 1, rotate: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                    className="relative bg-slate-800 border-2 border-emerald-500/50 rounded-2xl p-6 dark:shadow-2xl z-10 h-64 flex flex-col items-center justify-center"
                  >
                     <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mb-4">
                       <CheckCircle className="w-8 h-8 text-emerald-400" />
                     </div>
                     <div className="text-white font-bold text-center mb-2">Application Package Ready</div>
                     <div className="text-slate-400 text-xs text-center">Resume, Cover Letter, and Interview guide perfectly tailored.</div>
                     
                     <div className="mt-6 w-full flex gap-3 opacity-50">
                        <div className="h-8 bg-slate-700 rounded-lg flex-1" />
                        <div className="h-8 bg-slate-700 rounded-lg flex-1" />
                     </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
